import { currentState, currentUrl, onNavigate, scope, stateMap } from '@otto-ec/global-resources/navigation';
import type { SelectionPage, SelectionSource, SelectionState } from '@tilelist/application/ports/SelectionEvents.ts';
import { pageFromUrl } from './SelectionState.ts';

export type ForwardSelectionSource = Exclude<SelectionSource, 'historic'>;
export type SelectionNavigationInfo = {
    owner: 'dundee-selection';
    source: ForwardSelectionSource;
    initialize?: boolean;
    /*                                                                                      */
    pageMergeId?: string;
};

type NavigationEvent = Parameters<Parameters<typeof onNavigate.subscribe>[0]>[0];

export type SelectionNavigation = {
    currentCheckpoint(): unknown;
    initialSelection(bootstrapRule: string): SelectionState;
    navigate(
        selection: SelectionState,
        source: ForwardSelectionSource,
        history: 'push' | 'replace',
        initialize: boolean,
        pageMergeId?: string,
    ): Promise<void>;
    subscribe(handler: (event: NavigationEvent, checkpoint: unknown) => void): () => void;
};

export function navigationInfo(value: unknown): SelectionNavigationInfo | undefined {
    if (typeof value !== 'object' || value === null) return undefined;
    if (!('owner' in value) || value.owner !== 'dundee-selection') return undefined;
    if (!('source' in value) || (value.source !== 'page' && value.source !== 'full')) return undefined;
    return {
        owner: 'dundee-selection',
        source: value.source,
        ...('initialize' in value && value.initialize === true ? { initialize: true } : {}),
        ...('pageMergeId' in value && typeof value.pageMergeId === 'string' ? { pageMergeId: value.pageMergeId } : {}),
    };
}

function selectionInfo(
    source: ForwardSelectionSource,
    initialize: boolean,
    pageMergeId: string | undefined,
): SelectionNavigationInfo {
    return {
        owner: 'dundee-selection',
        source,
        ...(initialize ? { initialize: true } : {}),
        ...(pageMergeId ? { pageMergeId } : {}),
    };
}

export function isCheckpoint(value: unknown): value is SelectionState {
    if (typeof value !== 'object' || value === null) return false;
    return (
        'rule' in value &&
        typeof value.rule === 'string' &&
        value.rule.length > 0 &&
        'page' in value &&
        isSelectionPage(value.page) &&
        'url' in value &&
        typeof value.url === 'string' &&
        value.url.length > 0 &&
        'entryRule' in value &&
        typeof value.entryRule === 'string' &&
        value.entryRule.length > 0
    );
}

export function createTopicPageSelectionNavigation(): SelectionNavigation {
    const navigationScope = scope('dundee-selection', {
        state: stateMap<SelectionState>(),
    });

    return {
        currentCheckpoint: () => navigationScope.currentState.get().toObject(),
        initialSelection(bootstrapRule) {
            const checkpoint = navigationScope.currentState.get().toObject();
            if (isCheckpoint(checkpoint)) {
                return checkpoint;
            }
            const url = currentUrl.get().toString();
            const filteredRule = navigationScope.search().get('filtered');
            return {
                rule: filteredRule || bootstrapRule,
                page: pageFromUrl(url),
                url,
                entryRule: bootstrapRule,
            };
        },
        async navigate(selection, source, history, initialize, pageMergeId) {
            let url: string;
            if (!initialize) {
                if (selection.rule === selection.entryRule) {
                    navigationScope.search().delete('filtered');
                } else {
                    navigationScope.search().set('filtered', selection.rule);
                }
            }
            url = navigationScope.search().toString();
            await navigationScope.navigate(url, {
                history,
                merge: { search: false, hash: true },
                state: selection,
                rootState: { ...currentState.get(), entryRule: selection.entryRule },
                info: selectionInfo(source, initialize, pageMergeId),
            });
        },
        subscribe(handler) {
            return navigationScope.onNavigate.subscribe((event) => handler(event, event.to.state.toObject()));
        },
    };
}

export function defaultSelectionNavigation(): SelectionNavigation {
    const navigationScope = scope('dundee-selection', {
        state: stateMap<SelectionState>(),
    });

    return {
        currentCheckpoint: () => navigationScope.currentState.get().toObject(),
        initialSelection(bootstrapRule) {
            const checkpoint = navigationScope.currentState.get().toObject();
            if (isCheckpoint(checkpoint)) {
                return checkpoint;
            }
            const url = currentUrl.get().toString();
            return {
                rule: bootstrapRule,
                page: pageFromUrl(url),
                url,
                entryRule: bootstrapRule,
            };
        },
        navigate: async function (
            selection: SelectionState,
            source: ForwardSelectionSource,
            history: 'push' | 'replace',
            initialize: boolean,
            pageMergeId?: string,
        ): Promise<void> {
            await navigationScope.navigate(selection.url, {
                history,
                merge: { search: false, hash: true },
                state: selection,
                rootState: { ...currentState.get(), entryRule: selection.entryRule },
                info: selectionInfo(source, initialize, pageMergeId),
            });
        },
        subscribe(handler) {
            return navigationScope.onNavigate.subscribe((event) => handler(event, event.to.state.toObject()));
        },
    };
}

function isSelectionPage(value: unknown): value is SelectionPage {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
    return (
        Object.keys(value).every((key) => ['sortOrder', 'layout', 'offset', 'comparisonId'].includes(key)) &&
        (!('sortOrder' in value) || typeof value.sortOrder === 'string') &&
        (!('layout' in value) || typeof value.layout === 'string') &&
        (!('offset' in value) || typeof value.offset === 'string') &&
        (!('comparisonId' in value) || typeof value.comparisonId === 'string')
    );
}
